import { ChevronRightIcon, CoinsIcon, CreditCardIcon, RefreshCwIcon } from 'lucide-react';
import { Link } from 'react-router';

import { routes } from '@/assets/constants/routes-no.ts';
import { LoanExampleText } from '@/components/LoanExampleText/LoanExampleText.tsx';
import { Section } from '@/components/marketing/section/index.ts';
import { UnderlinedText } from '@/components/UnderlinedText/UnderlinedText.tsx';
import type { CompetitionWithConfig } from '@/lib/api/requests.ts';
import { CompetitionTile } from '@/pages/CompetitionWinners/CompetitionTile.tsx';

import HeroImage from './hero_doodle.webp';

const HeroDoodle = (props: { activeCompetition: CompetitionWithConfig | undefined }) => {
  return (
    <Section.Container className="bg-white text-v1-background-200">
      <Section.Content className="flex flex-col items-center py-8">
        <h1 className="mb-6 max-w-4xl text-center typography-v1-display text-balance xl:mt-12 [&_path]:stroke-3">
          <UnderlinedText distance="0.22em">Sammenlign</UnderlinedText> tilbud fra flere banker!
        </h1>
        <h2 className="mb-12 max-w-lg text-center typography-v1-body2 text-balance text-v1-neutral-100">
          Søk lån hos flere banker med Scoopr, velg tilbudet du liker. Søknadene er helt
          uforpliktende og gratis!
        </h2>

        <div className="flex w-full max-w-2xl flex-col gap-4">
          <ActionRowContent
            to={routes.PRODUCTS_REFINANCING}
            icon={RefreshCwIcon}
            title="Refinansiering"
            description="Sjekk om du kan få lavere rente"
          />
          <ActionRowContent
            to={routes.CREDIT_CARDS}
            icon={CreditCardIcon}
            title="Kredittkort"
            description="Finn det kredittkortet du trenger"
          />
          <ActionRowContent
            to={routes.CONSUMER_LOAN}
            icon={CoinsIcon}
            title="Forbrukslån"
            description="Søk lån til det du trenger"
          />

          <LoanExampleText className="mt-8 max-w-2xl text-center typography-v1-body4 text-v1-neutral-60" />

          {props.activeCompetition ? (
            <CompetitionTile
              className="mt-12 w-full"
              big
              competition={props.activeCompetition.competition}
              config={props.activeCompetition.config}
            />
          ) : (
            <div className="relative hidden w-full overflow-hidden min-[360px]:w-80 sm:block">
              <img
                alt=""
                className="absolute top-10 right-0 bottom-0 left-0 h-auto w-full object-cover"
                loading="lazy"
                src={HeroImage}
              />
            </div>
          )}
        </div>
      </Section.Content>
    </Section.Container>
  );
};

type IconComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;

const ActionRowContent = (props: {
  icon: IconComponent;
  title: string;
  description: string;
  to: string;
}) => {
  const Icon = props.icon;

  return (
    <Link
      to={props.to}
      className="group flex w-full cursor-pointer items-center justify-between rounded-2xl border border-v1-background-80 bg-white p-5 hover:bg-v1-background-20"
      style={{
        boxShadow: '0 -2px 10px 0 rgba(0, 0, 0, 0.03) inset, 2px 4px 25px 5px rgba(0, 0, 0, 0.05)',
      }}
    >
      <div className="flex min-w-0 flex-1 items-center gap-5">
        <div className="flex shrink-0 items-start rounded-full bg-v1-background-60 p-2.5">
          <Icon className="size-5 text-v1-primary-100" aria-hidden />
        </div>
        <div className="flex min-w-0 flex-col gap-0.5 text-left">
          <span className="typography-v1-body3 font-bold text-v1-background-200">
            {props.title}
          </span>
          <span className="typography-v1-body4 text-v1-neutral-80">{props.description}</span>
        </div>
      </div>
      <ChevronRightIcon className="size-6 shrink-0 text-v1-primary-100" aria-hidden />
    </Link>
  );
};

export default HeroDoodle;
